<script lang="ts">
	import deepEqual from "deep-equal";
	import { ButtonGroup, Dropdown, DropdownItem, Helper, Input } from "flowbite-svelte-v1";
	import { tick } from "svelte";
	import { SvelteSet } from "svelte/reactivity";
	import { fade } from "svelte/transition";
	import { twMerge } from "tailwind-merge";
	import { FieldType, type Field } from "#gen/forms/v1/field_pb.js";
	import Checkbox from "#lib/components/Checkbox.svelte";
	import Icon from "#lib/components/Icon.svelte";
	import Label from "#lib/components/Label.svelte";
	import P from "#lib/components/P.svelte";
	import EditorConfirmation from "#lib/components/flow/editor/EditorConfirmation.svelte";
	import MultiTokenInput from "#lib/components/page/components/form/validation/MultiTokenInput.svelte";
	import {
		OperandType,
		RuleOperator,
		charCountOptions,
		countOperators,
		defaultErrorMessage,
		digitCountOptions,
		durationOptions,
		generateSchema,
		getOperatorFullLabel,
		getOperatorLabel,
		getRegexPatternError,
		multiTokenTypes,
		operatorTypes,
		operators,
		parseSchema,
		relativeDateOperators,
		type BetweenValue,
		type DefineValidationSchemaType,
		type DurationUnit,
		type DurationValue,
		type Operator,
		type Rule,
		type RuleValueType,
	} from "#lib/components/page/components/form/validation/schemaBuilder.js";
	import FeatureFlagGate from "#lib/permissions/FeatureFlagGate.svelte";
	import Button from "#lib/ui/Button/index.js";

	interface Props {
		field: Field;
		disabled?: boolean;
		onChange?: (field: Field) => void;
	}

	let { field, onChange, disabled = false }: Props = $props();
	let parsedRules = $derived(field.validationSchema ? JSON.parse(field.validationSchema) : undefined);
	let fieldValidation: DefineValidationSchemaType = $derived(parseSchema(parsedRules));
	let operatorOptions = $derived(filterOperators(field));
	let checked = $derived(Boolean(fieldValidation.rules.length));
	let lastRule = $derived(fieldValidation?.rules[fieldValidation?.rules.length - 1]);
	let showMultiTokenHint = $state(false);

	const defaultRule: Rule = getDefaultRule();
	let activeRuleDropdowns: boolean[] = $state([]);
	let activeSecondaryRuleDropdowns: boolean[] = $state([]);
	let rulesContainer: HTMLDivElement | undefined = $state(undefined);
	let confirmDiscardOpen = $state(false);

	function getRegexError(value: RuleValueType): string | undefined {
		return typeof value === "string" ? getRegexPatternError(value) : undefined;
	}

	function filterOperators(field: Field | undefined) {
		const options = operatorTypes.map((t: Operator) => ({ name: t, value: t }));
		if (!field) {
			return options;
		}
		return options.filter(t => {
			const types = operators[t.value]?.operandTypes;
			if (!types) return true;
			let operandType = OperandType.TEXT;
			if (field.fieldType === FieldType.NUMBER) {
				operandType = OperandType.NUMBER;
			} else if (field.fieldType === FieldType.DATE) {
				operandType = OperandType.DATE;
			}
			return types.includes(operandType);
		});
	}

	function enableValidation(enabled: boolean) {
		if (enabled) {
			checked = enabled;
			fieldValidation!.rules = [defaultRule];
			handleChange();
			activeRuleDropdowns = new Array(fieldValidation.rules.length).fill(false);
		} else {
			confirmDiscardOpen = true;
			checked = true;
		}
	}

	function handleDiscard() {
		confirmDiscardOpen = false;
		fieldValidation!.rules = [];
		handleChange();
		activeRuleDropdowns = [];
		checked = false;
	}

	function setRuleType(ruleType: "all" | "any") {
		fieldValidation!.validationType = ruleType;
		handleChange();
	}

	function updateErrorMessage(value: string) {
		fieldValidation!.errorMessage = value.length > 0 ? value : defaultErrorMessage;
		handleChange();
	}

	function setRuleOperator(ruleOperator: RuleOperator, idx: number) {
		const changingValueType = operators[fieldValidation!.rules[idx].operator].valueType !== operators[ruleOperator].valueType;
		fieldValidation!.rules[idx].operator = ruleOperator;
		if (changingValueType) {
			fieldValidation!.rules[idx].value = undefined;
		}
		handleChange();
		activeRuleDropdowns[idx] = false;
	}

	function setCharCountType(charCountType: RuleOperator, idx: number) {
		setRuleOperator(charCountType, idx);
		activeSecondaryRuleDropdowns[idx] = false;
	}

	function setDurationType(durationType: DurationUnit, idx: number) {
		const durationValue = (fieldValidation!.rules[idx].value || {}) as DurationValue;
		durationValue.unit = durationType;
		fieldValidation!.rules[idx].value = durationValue;
		handleChange();
		activeSecondaryRuleDropdowns[idx] = false;
	}

	function handleChange() {
		const resolvedField = field;
		resolvedField.validationSchema = JSON.stringify(generateSchema(fieldValidation, field.fieldType));
		onChange?.(resolvedField);
	}

	function updateRuleValue(value: RuleValueType, idx: number, minMax?: "min" | "max") {
		const rule = fieldValidation!.rules[idx];
		if (field.fieldType === FieldType.NUMBER && rule.operator !== RuleOperator.REGEX) {
			const numValue = value === "" ? undefined : Number(value);
			const convertedValue = numValue !== undefined && Number.isNaN(numValue) ? undefined : numValue;
			if (rule.operator === RuleOperator.BETWEEN) {
				const currentValue = rule.value as BetweenValue | undefined;
				if (convertedValue && minMax) {
					const newValue: BetweenValue = currentValue ?? { min: undefined, max: undefined };
					newValue[minMax] = convertedValue;
					fieldValidation!.rules[idx].value = newValue;
				}
			} else {
				fieldValidation!.rules[idx].value = convertedValue;
			}
		} else if (field.fieldType === FieldType.DATE && value !== "") {
			if (relativeDateOperators.has(rule.operator)) {
				const durationValue = (rule.value || {}) as DurationValue;
				durationValue.value = Number(value);
				fieldValidation!.rules[idx].value = durationValue;
			} else if (rule.operator === RuleOperator.DATE_BETWEEN && minMax) {
				const betweenValue = (rule.value || {}) as BetweenValue;
				betweenValue[minMax] = value?.toString();
				fieldValidation!.rules[idx].value = betweenValue;
			} else {
				fieldValidation!.rules[idx].value = value;
			}
		} else {
			fieldValidation!.rules[idx].value = value;
		}
		handleChange();
		activeRuleDropdowns[idx] = false;
	}

	function ensureStringArrayValue(rule: { value: RuleValueType }): string[] {
		// Helper function to ensure the rule value is a string array
		if (Array.isArray(rule.value) && rule.value.every((item: string | number) => typeof item === "string")) {
			return rule.value;
		}
		if (typeof rule.value === "string" && rule.value.length > 0) {
			return rule.value
				.split("|")
				.map((s: string) => s.trim())
				.filter((s: string) => s.length > 0);
		}
		return [];
	}

	async function addRule() {
		if (fieldValidation && fieldValidation.rules.length > 0) {
			fieldValidation.rules = [...fieldValidation.rules, defaultRule];
			activeRuleDropdowns = [...activeRuleDropdowns, false];
		} else {
			fieldValidation.rules = [defaultRule];
		}

		handleChange();
		await tick();
		if (rulesContainer) {
			rulesContainer.scrollTop = rulesContainer.scrollHeight;
		}
	}

	function deleteRule(idx: number) {
		fieldValidation!.rules.splice(idx, 1);
		activeRuleDropdowns.splice(idx, 1);
		if (fieldValidation!.rules.length === 0) {
			checked = false;
		}
		handleChange();
	}

	function setRuleDropdownOpen(idx: number, open: boolean) {
		activeRuleDropdowns = activeRuleDropdowns.map((value, i) => (i === idx ? open : value));
	}

	function setSecondaryRuleDropdownOpen(idx: number, open: boolean) {
		activeSecondaryRuleDropdowns = activeSecondaryRuleDropdowns.map((value, i) => (i === idx ? open : value));
	}

	function handleInputBlur() {
		setTimeout(() => {
			const collapsedRules = fieldValidation.validationType === "any" ? collapseMultiStringValues(fieldValidation!.rules) : fieldValidation!.rules;
			if (!deepEqual(collapsedRules, fieldValidation!.rules)) {
				fieldValidation!.rules = collapsedRules;
				showMultiTokenHint = true;
				handleChange();
			}
		}, 500);
	}

	function collapseMultiStringValues(rules: Rule[]): Rule[] {
		if (field.fieldType === FieldType.NUMBER) {
			return rules;
		}
		const multiTokenOperators = [RuleOperator.CONTAINS, RuleOperator.NOT_CONTAINS, RuleOperator.STRING_EQ, RuleOperator.STRING_NEQ, RuleOperator.BEGINS, RuleOperator.ENDS];
		const groupedRules = {} as Record<RuleOperator, string[]>;

		for (const rule of rules) {
			if (multiTokenOperators.includes(rule.operator)) {
				if (!groupedRules[rule.operator]) {
					groupedRules[rule.operator] = [];
				}
				// Ensure rule.value is treated as an array
				const values = Array.isArray(rule.value) ? rule.value : [rule.value as string];
				groupedRules[rule.operator].push(...values);
			}
		}
		//group rules by operator while preserving order
		const resolvedRules: Rule[] = [];
		for (const rule of rules) {
			if (multiTokenOperators.includes(rule.operator)) {
				const strings = groupedRules[rule.operator];
				const uniqueStrings = [...new SvelteSet(strings)];
				if (!resolvedRules.find(r => r.operator === rule.operator)) {
					resolvedRules.push({ operator: rule.operator, value: uniqueStrings });
				} else if (rule.value && Array.isArray(rule.value) && rule.value.length === 0) {
					// don't remove new rules immediately
					resolvedRules.push({ operator: rule.operator, value: [] });
				}
			} else {
				resolvedRules.push(rule);
			}
		}
		return resolvedRules;
	}

	function formatInputValue(value: RuleValueType, isNumberInput?: boolean) {
		if (isNumberInput) {
			return value ? Number(value) : undefined;
		}
		return String(value || "");
	}

	function getDefaultRule(): Rule {
		if (field.fieldType === FieldType.NUMBER) {
			return { operator: RuleOperator.NUM_EQ, value: undefined };
		}
		if (field.fieldType === FieldType.DATE) {
			return { operator: RuleOperator.DATE_EQ, value: "" };
		}
		return { operator: RuleOperator.CONTAINS, value: [] };
	}
</script>

<FeatureFlagGate flag="setting_form_input_validation">
	{#if [FieldType.TEXT, FieldType.EMAIL, FieldType.NAME, FieldType.NUMBER, FieldType.DATE].includes(field.fieldType)}
		<div class="flex h-10.5 flex-row items-center gap-3" class:items-center={disabled && !field.description}>
			<Checkbox {disabled} class="shrink-0 font-normal" bind:checked on:change={e => enableValidation((e.target as HTMLInputElement).checked)} data-action-name="form-field-define-validation-checkbox">Input validation</Checkbox>
		</div>
		{#if checked && fieldValidation}
			<div class=" flex w-full flex-col rounded-lg bg-gray-50 p-4">
				<P size="sm" class="mb-2.5 font-medium text-black">Define rules</P>
				<div class="flex max-h-42.75 flex-col gap-3 overflow-y-auto py-1" bind:this={rulesContainer}>
					{#each fieldValidation?.rules as rule, idx (idx)}
						{@const isRegex = rule.operator === RuleOperator.REGEX}
						{@const regexError = isRegex ? getRegexError(rule.value) : undefined}
						<div class="flex flex-row items-center gap-2">
							{#if idx !== 0}
								<ButtonGroup class="ml-0.5 h-5.5" size="sm" role="group" aria-label="Combine rules with">
									<Button
										{disabled}
										data-action-name="validation-rule-type-operator"
										data-action-title="AND"
										color="alternative"
										class={twMerge("w-8.5 rounded-l! px-2 py-1 leading-6 text-black", fieldValidation?.validationType === "any" ? "text-black hover:bg-white" : "bg-gray-100 text-gray-400 hover:text-gray-900")}
										aria-pressed={fieldValidation?.validationType === "any"}
										on:click={() => setRuleType("any")}
									>
										<P size="xs" class="font-semibold text-inherit">OR</P>
									</Button>
									<Button
										{disabled}
										data-action-name="validation-rule-type-operator"
										data-action-title="OR"
										color="alternative"
										class={twMerge("w-8.5 rounded-r!  px-2 py-1 leading-4.5 text-black", fieldValidation?.validationType === "all" ? "text-black  hover:bg-white" : "bg-gray-100 text-gray-400 hover:text-gray-900")}
										aria-pressed={fieldValidation?.validationType === "all"}
										on:click={() => setRuleType("all")}
									>
										<P size="xs" class="font-semibold text-inherit">AND</P>
									</Button>
								</ButtonGroup>
							{/if}
							<P size="xs" class="shrink-0 font-medium whitespace-nowrap text-black">{idx === 0 ? "Input " : ""}must</P>
							<ButtonGroup class="flex-1 items-stretch" {disabled}>
								{@const stringTokens: string[] = ensureStringArrayValue(rule)}
								<Button data-action-name="validation-rule-type-operator-select" color="alternative" class={twMerge("flex shrink-0 self-stretch px-2.5 py-2", stringTokens.length > 0 ? "" : "shrink-0")} {disabled} aria-haspopup="true" aria-expanded={activeRuleDropdowns[idx]} on:click={() => setRuleDropdownOpen(idx, !activeRuleDropdowns[idx])}>
									<P class={twMerge("mr-auto max-w-23.5 truncate text-left text-xs font-normal", rule.operator ? "text-gray-900" : "text-gray-500")}>{rule.operator ? getOperatorLabel(rule.operator, stringTokens.length) : "Select operator"}</P>
									<Icon name="chevron-down" class="ml-2 h-2.5 w-2.5 flex-none" />
								</Button>
								<Dropdown class="z-50" isOpen={activeRuleDropdowns[idx]} simple transitionParams={{ duration: 0 }}>
									{#each operatorOptions as item (`${item.value}-${idx}`)}
										<DropdownItem onclick={() => setRuleOperator(item.value, idx)} data-action-name="validation-rule-type-operator" data-action-title={getOperatorFullLabel(item.value, field.fieldType, stringTokens.length)} class="w-full">
											<P size="xs" class="text-gray-700">{getOperatorFullLabel(item.value, field.fieldType, stringTokens.length)}</P>
										</DropdownItem>
									{/each}
								</Dropdown>
								<div class="bg-gray-0 w-full max-w-full flex-1 border-y px-2 py-1">
									{#if countOperators.includes(rule.operator)}
										{@const countOptions = field.fieldType === FieldType.NUMBER ? digitCountOptions : charCountOptions}
										<div class="flex flex-row items-center gap-2">
											<Button data-action-name="validation-rule-char-count-select" color="alternative" class="rounded-md px-2.5 py-2" {disabled} aria-haspopup="true" aria-expanded={activeSecondaryRuleDropdowns[idx]} on:click={() => setSecondaryRuleDropdownOpen(idx, !activeSecondaryRuleDropdowns[idx])}>
												<P class={twMerge("mr-auto truncate text-left text-xs font-normal whitespace-nowrap", rule.operator ? "text-gray-900" : "text-gray-500")}>{rule.operator ? countOptions.find(v => v.value === rule.operator)?.label : "Select operator"}</P>
												<Icon name="chevron-down" class="ml-2 h-2.5 w-2.5 flex-none" />
											</Button>
											<Dropdown class="z-50" simple transitionParams={{ duration: 0 }} isOpen={activeSecondaryRuleDropdowns[idx]}>
												{#each countOptions as item (`${item.label}-${idx}`)}
													<DropdownItem onclick={() => setCharCountType(item.value, idx)} data-action-name="validation-rule-type-operator" data-action-title={item.label} class="w-full">
														<P size="xs" class="shrink-0 whitespace-nowrap text-gray-700">{item.label}</P>
													</DropdownItem>
												{/each}
											</Dropdown>
											<Input min={0} type="number" value={rule.value ? Number(rule.value) : undefined} placeholder="100" class="h-8.5 rounded-md text-xs" onblur={e => updateRuleValue((e.target as HTMLInputElement).value, idx)} data-action-name="validation-rule-type-operator-value" data-action-title="Value" />
											<P size="xs" class="font-medium">{field.fieldType === FieldType.NUMBER ? "digits" : "characters"}</P>
										</div>
									{:else if rule.operator === RuleOperator.BETWEEN || rule.operator === RuleOperator.DATE_BETWEEN}
										{@const betweenValue = rule.value as BetweenValue | undefined}
										{#if field.fieldType === FieldType.NUMBER}
											<div class="flex flex-row items-center gap-2">
												<Input type="number" placeholder="0" value={betweenValue?.min} class="h-8.5 rounded-md text-xs" onblur={e => updateRuleValue((e.target as HTMLInputElement).value, idx, "min")} data-action-name="validation-rule-type-operator-value" data-action-title="Start value" />
												<P size="xs" class="font-medium">and</P>
												<Input type="number" placeholder="10" value={betweenValue?.max} class="h-8.5 rounded-md text-xs" onblur={e => updateRuleValue((e.target as HTMLInputElement).value, idx, "max")} data-action-name="validation-rule-type-operator-value" data-action-title="End value" />
											</div>
										{:else if field.fieldType === FieldType.DATE}
											<div class="flex flex-row items-center gap-2">
												<Input type="date" value={betweenValue?.min} max={betweenValue?.max} class="h-8.5 rounded-md text-xs" onchange={e => updateRuleValue((e.target as HTMLInputElement).value, idx, "min")} data-action-name="validation-rule-type-operator-value-min" data-action-title="Start value" />
												<P size="xs" class="font-medium">and</P>
												<Input type="date" value={betweenValue?.max} min={betweenValue?.min} class="h-8.5 rounded-md text-xs" onchange={e => updateRuleValue((e.target as HTMLInputElement).value, idx, "max")} data-action-name="validation-rule-type-operator-value-max" data-action-title="End value" />
											</div>
										{/if}
									{:else if field.fieldType === FieldType.DATE}
										{#if relativeDateOperators.has(rule.operator)}
											{@const durationValue = rule.value as DurationValue | undefined}
											<div class="flex flex-row items-center gap-2">
												<Input min={0} type="number" placeholder="Enter number" value={durationValue?.value ? Number(durationValue?.value) : undefined} class="h-8.5 w-32! rounded-md text-xs" onblur={e => updateRuleValue((e.target as HTMLInputElement).value, idx)} data-action-name="validation-rule-type-operator-value" data-action-title="Value" />
												<Button data-action-name="validation-rule-type-duration-operator-value" color="alternative" class="rounded-md px-2.5 py-2" {disabled} aria-haspopup="true" aria-expanded={activeSecondaryRuleDropdowns[idx]} on:click={() => setSecondaryRuleDropdownOpen(idx, !activeSecondaryRuleDropdowns[idx])}>
													<P class={twMerge("mr-auto truncate text-left text-xs font-normal whitespace-nowrap", durationValue?.unit ? "text-gray-900" : "text-gray-500")}>{durationValue?.unit ? durationOptions.find(v => v === durationValue?.unit) : "Select"}</P>
													<Icon name="chevron-down" class="ml-2 h-2.5 w-2.5 flex-none" />
												</Button>
												<Dropdown class="z-50" simple transitionParams={{ duration: 0 }} isOpen={activeSecondaryRuleDropdowns[idx]}>
													{#each durationOptions as item (`${item}-${idx}`)}
														<DropdownItem onclick={() => setDurationType(item, idx)} data-action-name="validation-rule-type-operator" data-action-title={item} class="w-full">
															<P size="xs" class="shrink-0 whitespace-nowrap text-gray-700">{item}</P>
														</DropdownItem>
													{/each}
												</Dropdown>
												<P size="xs" class="font-medium" whitespace="nowrap">{rule.operator === RuleOperator.DATE_AT_LEAST || rule.operator === RuleOperator.DATE_NO_MORE_THAN ? "before today" : "after today"}</P>
											</div>
										{:else}
											<Input type="date" value={formatInputValue(rule.value)} class="h-8.5 rounded-md text-xs" onchange={e => updateRuleValue((e.target as HTMLInputElement).value, idx)} data-action-name="validation-rule-type-operator-value" data-action-title="Value" {disabled} />
										{/if}
									{:else if multiTokenTypes.includes(rule.operator)}
										{@const stringTokens: string[] = ensureStringArrayValue(rule)}
										<div>
											<MultiTokenInput {disabled} {stringTokens} context="form-validation" onchange={tokens => updateRuleValue(tokens, idx)} useGrayOutline={true} onBlur={handleInputBlur} />
										</div>
									{:else}
										{@const isNumberInput = field.fieldType === FieldType.NUMBER && rule.operator !== RuleOperator.REGEX}
										<Input
											type={isNumberInput ? "number" : "text"}
											value={formatInputValue(rule.value, isNumberInput)}
											class="h-8.5  rounded-md text-xs"
											color={regexError ? "red" : "default"}
											placeholder={isRegex ? String.raw`e.g. ^\d{5}$ (no slashes or flags)` : isNumberInput ? "100" : "Enter value"}
											onblur={e => updateRuleValue((e.target as HTMLInputElement).value, idx)}
											data-action-name="validation-rule-type-operator-value"
											data-action-title="Value"
											{disabled}
										/>
									{/if}
								</div>
								<Button class="flex w-11 items-center justify-center self-stretch px-3" color="alternative" data-action-name="validation-rule-type-operator-delete" data-action-title="Delete rule" on:click={() => deleteRule(idx)} {disabled}>
									<span class="sr-only">Delete rule</span>
									<Icon name="x-mark" class="h-4 w-4" />
								</Button>
							</ButtonGroup>
							{#if idx === fieldValidation?.rules.length - 1}
								{@const canAddRule = !(lastRule && multiTokenTypes.includes(lastRule.operator) && lastRule.value && Array.isArray(lastRule.value) && lastRule.value.length > 1)}
								{#if canAddRule}
									<Button class="flex h-11 w-11 items-center justify-center px-3" color="alternative" data-action-name="validation-rule-type-operator-add" data-action-title="Add rule" on:click={addRule} {disabled}>
										<span class="sr-only">Add rule</span>
										<Icon name="plus" class="h-full w-full" />
									</Button>
								{/if}
							{/if}
						</div>
						{#if isRegex && regexError}
							<Helper color="red">
								<span class="text-xs">{regexError}</span>
							</Helper>
						{/if}
					{/each}
					{#if field.fieldType === FieldType.DATE && fieldValidation?.rules.some(r => relativeDateOperators.has(r.operator))}
						<div class="border-primary-100 col-span-full flex h-9 justify-between gap-3 rounded-lg bg-gray-100 px-4 py-3">
							<div class="flex items-center gap-3 text-gray-600">
								<Icon name="information-circle" class="h-6 w-6 min-w-6" />
								<P size="xs">"<strong>Today</strong>" refers to the date when a consumer interacts with this flow.</P>
							</div>
						</div>
					{/if}
					{#if fieldValidation?.rules.length > 0 && multiTokenTypes.includes(lastRule?.operator) && showMultiTokenHint}
						<div class="flex w-full items-center gap-2 rounded bg-gray-100 px-4 py-2" transition:fade={{ duration: 500 }}>
							<Icon name="light-bulb" class="h-4 w-4 text-gray-500" />
							<P class="text-center text-xs" secondary><strong>Entering a list?</strong> Copy and paste it into the input field, separated by “enter”</P>
						</div>
					{/if}
				</div>
				<div class="my-4 w-full justify-center border-t"></div>
				<Label for="errorMsg" class="mb-2 text-xs text-gray-900 dark:text-gray-300">If rules not met, then show error message</Label>
				<Input {disabled} type="text" id="errorMsg" class="bg-gray-0" value={fieldValidation?.errorMessage} placeholder="Your error message" onblur={e => updateErrorMessage((e.target as HTMLInputElement).value)} data-action-name="org-settings-field" data-action-title="Error message" />
			</div>
		{/if}
		{#if confirmDiscardOpen}
			<EditorConfirmation buttonDataActionName="validation-confirm-discard" bind:open={confirmDiscardOpen} onContinue={handleDiscard} onCancel={() => (confirmDiscardOpen = false)} confirmProps={{ color: "red" }}>
				<P class="text-center" color="text-gray-800">Are you sure you want to delete the input rules you’ve set?</P>
			</EditorConfirmation>
		{/if}
	{/if}
</FeatureFlagGate>
